import { useEffect, useState, useCallback } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { CommentableParagraphNode } from '../../nodes/CommentableParagraphNode';
import { CommentableHeadingNode } from '../../nodes/CommentableHeadingNode';

import { $getNodeByKey, $nodesOfType } from 'lexical';
import { reactArticle, getListReactOfParagraph } from '../../../../apis/article.api';
import { CommentableListNode } from '../../nodes/CommentableListNode';
import DataLayer from '../../../../utils/data-layer';
import { UnauthorizedDispatcher } from 'src/utils/unauthorized-dispatcher';

type ReactionEventDetail = {
  articleId: string;
  nodeId: string;
  nodeKey: string;
  reactionType: string;
  isLike: boolean;
};

type ReactionsPluginProps = {
  article?: any;
  articleId?: string;
};

const ReactionsPlugin = ({ article, articleId: articleIdProp }: ReactionsPluginProps) => {
  const [editor] = useLexicalComposerContext();
  const [pendingReactions, setPendingReactions] = useState<Set<string>>(new Set());
  const userId = JSON.parse(localStorage.getItem('user'))?.id;

  const handleNodeReaction = useCallback(
    async (e: CustomEvent<ReactionEventDetail>) => {
      if (!userId) return UnauthorizedDispatcher();

      const { nodeId, nodeKey, reactionType, isLike } = e.detail;

      if (pendingReactions.has(nodeId)) {
        return;
      }

      setPendingReactions((prev) => new Set(prev).add(nodeId));

      try {
        const effectiveArticleId = articleIdProp ?? article?.id;
        const res = await reactArticle({
          articleId: effectiveArticleId,
          nodeId,
          reactionType,
          isLike
        });

        // Track paragraph react event
        if (effectiveArticleId) {
          const articleType = article?.writingType?.name;
          DataLayer.trackParagraphReact(articleType, effectiveArticleId);
        }

        // Fetch detailed reaction breakdown for the UI
        const detailedReactions = await getListReactOfParagraph(
          nodeId,
          effectiveArticleId || undefined
        );

        editor.update(() => {
          const node = $getNodeByKey(nodeKey);

          if (node instanceof CommentableParagraphNode) {
            // Get current data and preserve comment info
            const currentData = node.getNodeData();

            const updatedNodeData = {
              ...currentData, // Preserve existing data
              isReacted: res.isReacted,
              reactedType: res.reactType,
              reactionCount: Number(res.reactionCount),
              reactionCounts: {
                like: detailedReactions.like || 0,
                funny: detailedReactions.funny || 0,
                angry: detailedReactions.angry || 0,
                sad: detailedReactions.sad || 0,
                innovative: detailedReactions.innovative || 0,
                big_brain: detailedReactions.big_brain || 0,
                need_source: detailedReactions.need_source || 0,
                poor_logic: detailedReactions.poor_logic || 0
              },
              timestamp: Date.now() // Add timestamp for change detection
            };

            node.setParagraph(updatedNodeData);
          } else if (node instanceof CommentableHeadingNode) {
            const currentData = node.getNodeData();
            const updatedNodeData = {
              ...currentData,
              isReacted: res.isReacted,
              reactedType: res.reactType,
              reactionCount: Number(res.reactionCount),
              reactionCounts: {
                like: detailedReactions.like || 0,
                funny: detailedReactions.funny || 0,
                angry: detailedReactions.angry || 0,
                sad: detailedReactions.sad || 0,
                innovative: detailedReactions.innovative || 0,
                big_brain: detailedReactions.big_brain || 0,
                need_source: detailedReactions.need_source || 0,
                poor_logic: detailedReactions.poor_logic || 0
              }
            };
            node.setNodeData(updatedNodeData);
          } else if (node instanceof CommentableListNode) {
            const currentData = node.getNodeData();
            const updatedNodeData = {
              ...currentData,
              isReacted: res.isReacted,
              reactedType: res.reactType,
              reactionCount: Number(res.reactionCount),
              reactionCounts: {
                like: detailedReactions.like || 0,
                funny: detailedReactions.funny || 0,
                angry: detailedReactions.angry || 0,
                sad: detailedReactions.sad || 0,
                innovative: detailedReactions.innovative || 0,
                big_brain: detailedReactions.big_brain || 0,
                need_source: detailedReactions.need_source || 0,
                poor_logic: detailedReactions.poor_logic || 0
              }
            };
            node.setNodeData(updatedNodeData);
          }
        });
      } catch (error) {
        console.error('Error submitting reaction:', error);
      } finally {
        setPendingReactions((prev) => {
          const next = new Set(prev);
          next.delete(nodeId);
          return next;
        });
      }
    },
    [editor, pendingReactions, articleIdProp, article]
  );

  useEffect(() => {
    const rootElement = editor.getRootElement();
    if (rootElement) {
      // Listen for both paragraph reactions and general node reactions
      rootElement.addEventListener('paragraphReaction', handleNodeReaction as EventListener);
      rootElement.addEventListener('nodeReaction', handleNodeReaction as EventListener);
    }

    return () => {
      if (rootElement) {
        rootElement.removeEventListener('paragraphReaction', handleNodeReaction as EventListener);
        rootElement.removeEventListener('nodeReaction', handleNodeReaction as EventListener);
      }
    };
  }, [editor, handleNodeReaction]);

  return null;
};

export default ReactionsPlugin;
